import { NavLink } from '@mantine/core';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router';
import useAllowedActions from '../../helpers/useAllowedActions';
import { setSuggestSubscriptionAlert } from '../../store/subscriptionSlice';
import UnlockFeatureButton from './UnlockFeatureButton';

const containsAllSearch = (linkSearch, currentLocationSearch) => {
  const linkParams = new URLSearchParams(linkSearch);
  const currentLocationSParams = new URLSearchParams(currentLocationSearch);

  for (const [name, value] of linkParams.entries()) {
    if (currentLocationSParams.get(name) !== value) {
      return false;
    }
  }
  return true;
};

const isActivePath = (locationPathname, linkPathname, linkSearch, locationSearch) => {
  // Check if the current path starts with the link path
  const pathMatches = locationPathname.startsWith(linkPathname);

  if (!pathMatches) {
    return false;
  }

  const linkParams = new URLSearchParams(linkSearch);
  const linkParamsEntries = Array.from(linkParams.entries());
  const hasLinkParams = linkParamsEntries.length > 0;

  // Special handling for links with 'type' parameter (like pages?type=recommenders or pages?type=bannerBlocks)
  // These need to match based on the URL structure after /pages/
  const typeParam = linkParams.get('type');
  if (typeParam) {
    // Extract the part after /pages/ in the location pathname
    const pagesMatch = locationPathname.match(/\/pages\/\d+\/(\w+)/);
    if (pagesMatch) {
      const urlSegment = pagesMatch[1]; // e.g., 'recommenders' or 'bannerBlocks'
      return urlSegment === typeParam;
    }
    // If we're on /pages without a subpath, check the query param
    if (locationPathname.endsWith('/pages')) {
      return containsAllSearch(linkSearch, locationSearch);
    }
    return false;
  }

  // Special handling for links with 'mode' parameter (campaigns and workflows)
  // These should always require the mode param to match
  const modeParam = linkParams.get('mode');
  if (modeParam) {
    return containsAllSearch(linkSearch, locationSearch);
  }

  // For other links with search params
  if (hasLinkParams) {
    // If we're on the exact path, check query params
    if (locationPathname === linkPathname) {
      return containsAllSearch(linkSearch, locationSearch);
    }
    // For subpages, just match by path
    return true;
  }

  return true;
};

const LockableLinkContainer = ({ restrictionKey, icon, name, to, restrictedNavigation, ...rest }) => {
  const { domain } = useSelector((state) => state.domain);
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const isSuperAdmin = user.userRoles.some((role) => role.role === 'superAdmin');

  const toPrefixed = '/accounts/' + domain.accountId + '/domains/' + domain.id + to;

  const location = useLocation();
  const linkUrl = new URL(toPrefixed, window.location.origin);

  const allowedActions = useAllowedActions(domain);

  const restricted =
    restrictionKey &&
    allowedActions &&
    !allowedActions.has(restrictionKey) &&
    !allowedActions.has('*') &&
    !isSuperAdmin;
  const active = isActivePath(location.pathname, linkUrl.pathname, linkUrl.search, location.search);
  const isDisabled = restricted && restrictedNavigation;

  const alertSubscription = active && restricted;

  useEffect(() => {
    // Only dispatch when setting to true - don't clear other components' alerts
    if (alertSubscription) {
      dispatch(setSuggestSubscriptionAlert(true));
    }
  }, [alertSubscription, dispatch]);

  const handleClick = (e) => {
    if (isDisabled) {
      e.preventDefault();
    }
  };

  return (
    <NavLink
      component={Link}
      to={{ pathname: linkUrl.pathname, search: linkUrl.search }}
      active={active}
      disabled={isDisabled}
      onClick={handleClick}
      styles={{
        root: !active ? { color: 'var(--mantine-color-primary-filled)' } : {},
        label: !active ? { color: 'var(--mantine-color-primary-filled)' } : {},
        section: !active ? { color: 'var(--mantine-color-primary-filled)' } : {},
      }}
      label={
        <>
          {name}
          {restricted && (domain.account.canManageSubscriptions || isSuperAdmin) && (
            <UnlockFeatureButton
              size={'lg'}
              restrictionKey={restrictedNavigation ? restrictionKey : restrictionKey.replace('List', 'Create')}
            />
          )}
        </>
      }
      leftSection={icon}
      {...rest}
    />
  );
};

export default LockableLinkContainer;
